import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserDetails, updateUserDetails, deleteAccount } from '../api/userApi';
import { SquarePen } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface UserDetails {
  id: number;
  email: string;
  nickname: string;
  phone_number?: string;
  created_at: string;
  updated_at: string;
  is_certified: boolean;
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editForm, setEditForm] = useState({
    nickname: '',
    phone_number: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteChecklist, setDeleteChecklist] = useState({
    dataLoss: false,
    noRefund: false,
    finalConfirm: false
  });

  useEffect(() => {
    fetchUserDetails();
  }, []);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const response = await getUserDetails();
      
      // 서버 응답 구조에 맞게 데이터 추출
      const userData = response.body || response;
      setUserDetails(userData);
      setEditForm({
        nickname: userData.nickname || '',
        phone_number: userData.phone_number || ''
      });
    } catch (err: any) {
      setError(err.message || '사용자 정보를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      await updateUserDetails({
        nickname: editForm.nickname,
        phone: editForm.phone_number
      });
      alert('정보가 성공적으로 업데이트되었습니다.');
      setIsEditing(false);
      fetchUserDetails(); // 업데이트된 정보 다시 불러오기
    } catch (err: any) {
      setError(err.message || '정보 업데이트에 실패했습니다.');
    }
  };

  const handleCancel = () => {
    setEditForm({
      nickname: userDetails?.nickname || '',
      phone_number: userDetails?.phone_number || ''
    });
    setIsEditing(false);
  };

  const handleDeleteAccount = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    const allChecked = Object.values(deleteChecklist).every(checked => checked);
    
    if (!allChecked) {
      alert('모든 확인사항에 동의해주세요.');
      return;
    }

    try {
      await deleteAccount();
      alert('회원탈퇴가 완료되었습니다.');
      localStorage.removeItem('user_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('userId');
      navigate('/login');
    } catch (err: any) {
      setError(err.message || '회원탈퇴에 실패했습니다.');
    } finally {
      setShowDeleteModal(false);
      setDeleteChecklist({
        dataLoss: false,
        noRefund: false,
        finalConfirm: false
      });
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteChecklist({
      dataLoss: false,
      noRefund: false,
      finalConfirm: false
    });
  };

  const handleCheckboxChange = (key: keyof typeof deleteChecklist) => {
    setDeleteChecklist(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleLogout = () => {
    localStorage.removeItem('user_token');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">사용자 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={fetchUserDetails}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>마이페이지 | BIN-GO</title>
        <meta name="description" content="나의 프로필, 개인정보 및 계정 관리" />
        <meta property="og:title" content="마이페이지 | BIN-GO" />
        <meta property="og:description" content="나의 정보와 계정 전체 관리 페이지." />
      </Helmet>
      <div className="max-w-2xl mx-auto p-6">
        <h1 className="text-2xl font-bold mb-6">마이페이지</h1>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">개인정보</h2>
          
            {!isEditing && (
              <SquarePen
              className="text-gray-600 hover:text-blue-600 cursor-pointer w-5 h-5"
              onClick={handleEdit}
            />
            )}
          </div>

          <div className="space-y-4">
            {/* 이메일 (수정 불가) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
              <input
                type="email"
                value={userDetails?.email || ''}
                disabled
                className="w-full border border-gray-300 rounded px-4 py-2 bg-gray-100 text-gray-500"
              />
            </div>

            {/* 닉네임 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">닉네임</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.nickname}
                  onChange={(e) => setEditForm({ ...editForm, nickname: e.target.value })}
                  className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="닉네임을 입력하세요"
                />
              ) : (
                <input
                  type="text"
                  value={userDetails?.nickname || ''}
                  disabled
                  className="w-full border border-gray-300 rounded px-4 py-2 bg-gray-100 text-gray-500"
                />
              )}
            </div>

            {/* 전화번호 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">전화번호</label>
              <input
                  type="tel"
                  value={userDetails?.phone_number || '등록된 전화번호가 없습니다'}
                  disabled
                  className="w-full border border-gray-300 rounded px-4 py-2 bg-gray-100 text-gray-500"
                />
            </div>

            {/* 가입일 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">가입일</label>
              <input
                type="text"
                value={userDetails?.created_at ? new Date(userDetails.created_at).toLocaleDateString('ko-KR') : ''}
                disabled
                className="w-full border border-gray-300 rounded px-4 py-2 bg-gray-100 text-gray-500"
              />
            </div>

            {/* 인증 상태 */}
    <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">호스트 계좌 인증</label>
              <div className="flex items-center">
                <span className={`px-2 py-1 rounded text-sm ${
                  userDetails?.is_certified 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {userDetails?.is_certified ? '인증완료' : '미인증'}
                </span>
              </div>
            </div>
          </div>

          {/* 수정 모드 버튼들 */}
          {isEditing && (
            <div className="flex gap-2 mt-6">
              <button
                onClick={handleSave}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
              >
                저장
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
              >
                취소
              </button>
            </div>
          )}
        </div>

        {/* 계정 관리 */}
        <div className="bg-white rounded-lg shadow-md p-6 mt-6">
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/host/account-verify')}
              className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-lg text-sm font-medium transition-colors"
            >
              계좌인증
            </button>
            
            <button
              onClick={handleDeleteAccount}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-lg text-sm font-medium transition-colors"
            >
              회원탈퇴
            </button>
          </div>
        </div>
      </div>

      {/* 회원탈퇴 모달 */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4 text-red-600">회원탈퇴</h3>
            <p className="text-gray-600 mb-6">
              회원탈퇴를 진행하기 전에 다음 사항들을 확인해주세요.
            </p>
            
            <div className="space-y-4 mb-6">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="dataLoss"
                  checked={deleteChecklist.dataLoss}
                  onChange={() => handleCheckboxChange('dataLoss')}
                  className="mt-1 mr-3"
                />
                <label htmlFor="dataLoss" className="text-sm text-gray-700">
                  <strong>모든 데이터 삭제:</strong> 등록한 공간, 예약 내역, 결제 정보 등 모든 데이터가 영구적으로 삭제됩니다.
                </label>
              </div>
              
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="noRefund"
                  checked={deleteChecklist.noRefund}
                  onChange={() => handleCheckboxChange('noRefund')}
                  className="mt-1 mr-3"
                />
                <label htmlFor="noRefund" className="text-sm text-gray-700">
                  <strong>복구 불가:</strong> 탈퇴 후에는 데이터를 복구할 수 없으며, 동일한 이메일로 재가입이 제한될 수 있습니다.
                </label>
              </div>
              
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="finalConfirm"
                  checked={deleteChecklist.finalConfirm}
                  onChange={() => handleCheckboxChange('finalConfirm')}
                  className="mt-1 mr-3"
                />
                <label htmlFor="finalConfirm" className="text-sm text-gray-700">
                  <strong>최종 확인:</strong> 위 내용을 모두 이해했으며, 회원탈퇴를 진행하겠습니다.
                </label>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleDeleteCancel}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded"
              >
                취소
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded"
              >
                탈퇴하기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;